/**
 * Main state hook for the fix-errors page.
 * Delegates validation to useFixErrorValidation and API calls to useFixErrorApi.
 */

import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { formatDate } from '@/util/formatting';
import { useNavigationService } from '@/services/navigate-service';
import { ROUTES } from '@/config/routes';

import type { PropertyErrorFormData, OrderErrorFormData } from '@/types/property.types';
import type { ErrorCell } from '../utils/fixErrorFieldMaps';
import { PROPERTY_OPTIONAL_FIELDS, ORDER_OPTIONAL_FIELDS } from '../utils/fixErrorFieldMaps';
import { buildPropertyFormData, buildOrderFormData } from './useErrorDataTransform';
import { useFixErrorValidation } from './useFixErrorValidation';
import { useFixErrorApi } from './useFixErrorApi';
import type { FixErrorModal } from '../types/modal-state';
import { NO_MODAL } from '../types/modal-state';

// ---- Types ----------------------------------------------------------------

interface ErrorTableRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

interface FixErrorsProps {
  successData: string;
  failedData: string;
  dateInfo: string;
  uploadedByInfo: string;
  tableData: string;
  setActiveStep: (step: number) => void;
  setOrderCountInfo: (count: number) => void;
  setFailedData: (data: string) => void;
  setFileId: (id: string) => void;
  setAmount: (amount: number) => void;
  setSuccessData: (data: string) => void;
  setFixErrorTableData: (data: string) => void;
  fixErrorTableData: string | null;
  setSkippedStepFromStepper: (step: number) => void;
  fileState: boolean;
  fileId: string;
}

export interface FileDetail {
  label: string;
  value: string;
  stateType?: string;
}

// ---- Hook -----------------------------------------------------------------

export const useFixErrors = (props: FixErrorsProps) => {
  const {
    successData, failedData, dateInfo, uploadedByInfo, tableData,
    setActiveStep, setOrderCountInfo, setFailedData, setFileId,
    setAmount, setSuccessData, setFixErrorTableData,
    setSkippedStepFromStepper, fileState, fileId,
  } = props;

  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();

  const isProperty = searchParams.get('isProperty')?.toLowerCase() === 'true';

  // ---- State --------------------------------------------------------------

  const [loading, setLoading] = useState(false);
  const [popUpLoader, setPopUpLoader] = useState(false);
  const [fixIssueLoading, setFixIssueLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [modal, setModal] = useState<FixErrorModal>(NO_MODAL);
  const [popUpLoaderForOrder, setPopUpLoaderForOrder] = useState(false);
  const [fileIdForOrder, setFileIdForOrder] = useState(fileId);
  const [successDataAfterFix, setSuccessDataAfterFix] = useState<number>();
  const [fileChangeAfterFix, setFileChangeAfterFix] = useState(false);
  const [isClearData, setIsClearData] = useState(false);

  const [ErrorTableData, setErrorTableData] = useState<ErrorTableRow[]>(() => {
    if (isProperty) {
      try { return JSON.parse(searchParams.get('tableData') || '[]'); }
      catch { return []; }
    }
    try { return JSON.parse(tableData); }
    catch { return []; }
  });

  const [fixErrorTableDataForOrder] = useState<ErrorTableRow[] | undefined>();
  const [fixErrorTableDataForProperty] = useState<ErrorTableRow[] | undefined>();

  const [successCountAfterFix, setSuccessCountAfterFix] = useState(
    searchParams.get('success') != null ? Number(searchParams.get('success')) : 0
  );
  const [failedCountAfterFix, setFailedCountAfterFix] = useState<number | null>(null);
  const [result, setResult] = useState<ErrorTableRow[] | null>(ErrorTableData);

  // ---- Extracted hooks ----------------------------------------------------

  const validationCallbacks = useMemo(() => ({ setModal }), []);

  const { hasActiveItem } = useFixErrorValidation(isProperty, validationCallbacks);

  const apiCallbacks = useMemo(() => ({
    setLoading, setPopUpLoader, setPopUpLoaderForOrder, setModal,
    setErrorTableData, setResult,
    setFailedCountAfterFix, setSuccessCountAfterFix,
    setFixIssueLoading, setIsClearData, setFileChangeAfterFix,
    setFileIdForOrder, setSuccessDataAfterFix,
    setOrderCountInfo, setFailedData, setFileId, setAmount,
    setSuccessData, setFixErrorTableData, setSkippedStepFromStepper,
    setActiveStep,
  }), []);

  const { callBulkListUpdate, callBulkListUpdateForOrder, fixIssues: fixIssuesApi } =
    useFixErrorApi({
      isProperty, fileState, successCountAfterFix, callbacks: apiCallbacks,
    });

  // ---- Count helpers ------------------------------------------------------

  const getFailedCount = (): string | null =>
    isProperty
      ? (failedCountAfterFix === null ? searchParams.get('failed') : String(failedCountAfterFix))
      : failedData;

  const getSuccessCount = (): string | null =>
    isProperty
      ? (successCountAfterFix === 0 ? searchParams.get('success') : String(successCountAfterFix))
      : successData;

  const failed = getFailedCount();

  // ---- File details -------------------------------------------------------

  const pluralise = (count: string | number): string =>
    count === 1 || count === '1' ? '1 property' : `${count} properties`;

  const getFileDetails = useCallback((): FileDetail[] | null => {
    const success = getSuccessCount();
    const failedVal = getFailedCount();
    const date = isProperty ? searchParams.get('date') : dateInfo;
    let uploadedBy = isProperty ? searchParams.get('by') : uploadedByInfo;
    uploadedBy = uploadedBy === 'null' ? '-' : uploadedBy;
    const totalCount = String(Number(success) + Number(failedVal));

    if (!success || !failedVal || !date || !uploadedBy) return null;

    return [
      { label: 'Uploaded Date', value: formatDate(date) },
      { label: 'Uploaded By', value: uploadedBy },
      { label: isProperty ? 'Total Properties' : 'Total Orders', value: totalCount },
      {
        label: isProperty ? 'Import Success' : 'Order Accepted/Placed',
        value: isProperty ? pluralise(success) : `${success} ${fileState ? 'placed' : 'accepted'}`,
        stateType: 'success',
      },
      {
        label: isProperty ? 'Import Failed' : 'Order Rejected',
        value: isProperty ? pluralise(failedVal) : `${failedVal} rejected`,
        stateType: 'error',
      },
    ];
  }, [isProperty, successCountAfterFix, failedCountAfterFix, searchParams]);

  const getErrorMessage = (): string =>
    isProperty
      ? 'Please fix all the errors for Required fields to import the properties'
      : 'Fix the errors to continue placing order';

  // ---- Field nulling helpers -----------------------------------------------

  const setActiveFieldsToNull = (
    data: ErrorTableRow[] | string, fields: readonly string[]
  ): ErrorTableRow[] => {
    const parsed: ErrorTableRow[] = typeof data === 'string' ? JSON.parse(data) : data;
    parsed.forEach((row) => {
      fields.forEach((field) => {
        const cell = row[field] as ErrorCell | undefined;
        if (cell?.isActive) cell.value = null;
      });
    });
    return parsed;
  };

  const updateOrderFields = (data: ErrorTableRow[] | string): ErrorTableRow[] => {
    const orders: ErrorTableRow[] = typeof data === 'string' ? JSON.parse(data) : data;
    orders.forEach((order) => {
      const hasActiveOutside = Object.keys(order).some(
        (key) =>
          !(ORDER_OPTIONAL_FIELDS as readonly string[]).includes(key) &&
          (order[key] as ErrorCell)?.isActive === true
      );
      if (!hasActiveOutside) {
        (ORDER_OPTIONAL_FIELDS as readonly string[]).forEach((field) => {
          const cell = order[field] as ErrorCell | undefined;
          if (cell?.isActive) cell.value = null;
        });
      }
    });
    return orders;
  };

  // ---- Actions ------------------------------------------------------------

  const onImport = (): void => {
    if (!result || hasActiveItem(result)) return;

    if (isProperty) {
      const formData = buildPropertyFormData(result, searchParams.get('fileId'));
      setIsClearData(true);
      setFileChangeAfterFix(true);
      callBulkListUpdate(formData as PropertyErrorFormData[]);
    } else {
      const formData = buildOrderFormData(result, ErrorTableData);
      callBulkListUpdateForOrder(formData as OrderErrorFormData[]);
    }
  };

  const onCancel = (): void => {
    navigateTo(isProperty ? ROUTES.PROPERTIES_NEW_IMPORT : ROUTES.ORDERS_NEW_IMPORT);
  };

  const onBack = (): void => setActiveStep(1);

  const importPropertiesWithNull = (): void => {
    setPopUpLoader(true);
    const finalResult = setActiveFieldsToNull(result ?? [], PROPERTY_OPTIONAL_FIELDS);
    const formData = buildPropertyFormData(finalResult, searchParams.get('fileId'));
    setIsClearData(true);
    setFileChangeAfterFix(true);
    callBulkListUpdate(formData as PropertyErrorFormData[]);
  };

  const importOrdersWithNull = (): void => {
    setPopUpLoaderForOrder(true);
    const finalResult = setActiveFieldsToNull(result ?? [], ORDER_OPTIONAL_FIELDS);
    const formData = buildOrderFormData(finalResult, ErrorTableData);
    setIsClearData(true);
    setFileChangeAfterFix(true);
    callBulkListUpdateForOrder(formData as OrderErrorFormData[]);
  };

  const importOrdersWithNullPartially = (): void => {
    setPopUpLoaderForOrder(true);
    const finalResult = updateOrderFields(result ?? []);
    const formData = buildOrderFormData(finalResult, ErrorTableData);
    setIsClearData(true);
    setFileChangeAfterFix(true);
    callBulkListUpdateForOrder(formData as OrderErrorFormData[]);
  };

  const fixIssues = async (): Promise<void> => fixIssuesApi(fileIdForOrder);

  const checkAndContinue = (): void => {
    if (successDataAfterFix && successDataAfterFix > 0) {
      setActiveStep(3);
    } else {
      dispatch(showAlert({ type: Variants.ERROR, message: 'There is no valid orders to place' }));
    }
    setModal(NO_MODAL);
  };

  const dismissModal = useCallback((): void => setModal(NO_MODAL), []);

  return {
    isProperty, loading, disabled, ErrorTableData, result,
    isClearData, fileChangeAfterFix, fixErrorTableDataForOrder,
    fixErrorTableDataForProperty, successCountAfterFix, failedCountAfterFix,
    modal, dismissModal,
    fixIssueLoading, popUpLoader, popUpLoaderForOrder,
    setDisabled, setResult, setIsClearData, setFileChangeAfterFix,
    onImport, onCancel, onBack, fixIssues, checkAndContinue,
    importPropertiesWithNull, importOrdersWithNull,
    importOrdersWithNullPartially, getFileDetails, getErrorMessage, failed,
  };
};
